import React, { useEffect, useRef } from "react";
import { StyleSheet, View } from "react-native";
import { InputProps, Text } from "@ui-kitten/components";
import { FieldProps, getIn } from "formik";
import dayjs from "dayjs";
import { UserFieldDetails } from "features/types";
import Input from "components/Input";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";

type TextInputProp = { userFieldType: UserFieldDetails["type"] } & FieldProps &
  InputProps;

export const formattedDate = (value: string) => {
  if (!value) return value;
  value = value.replace(/[^\d]/g, "");
  if (value.length <= 2) return value;
  if (value.length <= 4) {
    return `${value.slice(0, 2)}/${value.slice(2)}`;
  }
  return `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
};

const TextInput = (props: TextInputProp) => {
  const {
    field: { name, onBlur, onChange, value },
    form: { errors, touched, setFieldTouched },
    appearance = "default",
    label,
    // @ts-ignore
    innerRef,
    userFieldType = "STRING",
    status = "basic",
    ...inputProps
  } = props;

  const errorMsg = touched[name] && getIn(errors, name);
  const isInitialized = useRef<boolean>(false);

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const timezone = tenantConfig?.timezone;

  // format birthdate and phone number fields when component loads
  useEffect(() => {
    if (userFieldType === "DATE" && value && isInitialized.current === false) {
      onChange(name)(
        formattedDate(
          dayjs(value)
            .tz(timezone || "")
            .format("MM/DD/YYYY")
        )
      );
      isInitialized.current = true;
    }

    return () => {
      isInitialized.current = false;
    };
  }, [isInitialized.current]);

  if (!timezone) return null;

  const handleChangeText = (text: string) => {
    onChange(name)(formattedDate(text));
  };
  return (
    <View style={{ marginBottom: 15 }}>
      <Input
        ref={innerRef}
        value={value || ""}
        onChangeText={handleChangeText}
        onBlur={() => {
          setFieldTouched(name);
          onBlur(name);
        }}
        label={label}
        {...inputProps}
        style={[
          appearance !== "default" ? styles.textInput : {},
          inputProps.style,
        ]}
        status={errorMsg ? "danger" : status}
        placeholder="MM/DD/YYYY"
        keyboardType="numeric"
        testID={name.toLowerCase()}
      ></Input>

      {/* only show error text for password fields */}
      {errorMsg && <Text style={styles.errorText}>{errorMsg}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  textInput: {
    borderWidth: 0,
    borderBottomWidth: 1,
    borderRadius: 0,
  },
  errorText: {
    color: "red",
    fontSize: 13,
    marginLeft: 5,
  },
  errorInput: {
    borderColor: "red",
  },
});

export default TextInput;
